/**
 * ReferralDirectory - Support Providing Organizations
 * 
 * STRUCTURE (matching other pages like EmergencyServices):
 * - Icon + Title at top
 * - Horizontal offence tabs
 * - Government / Non-Government sections below
 * - Back arrow at bottom
 */
import React, { useEffect, useState, useMemo, useRef } from "react";
import Header from "../components/Header";
import ModalAbout from "../components/ModalAbout";
import { makeStyles } from "@mui/styles";
import { Typography, CircularProgress } from "@mui/material";
import Options from "../components/Options";
import { changeTab } from "../redux/TabSlice";
import { useDispatch, useSelector } from "react-redux";
import clsx from "clsx";
import { getOrgContactList, getTitleFromAppContent, getContentTitle, getTranslatedItemTitle } from "../helpers/utils";
import { trackOffenceView, trackPageView } from "../helpers/api";
import { setSelectedItem, getOffences, getOrganizationsByOffence, setSelectedOffence } from "../redux/OrganizationSlice";
import { setSelectedProviderEng } from "../redux/AppContentSlice";
import { setBackToGuide } from "../redux/PageSlice";
import PageBackButton from "../components/PageBackButton";
import referralIcon from "../assets/img/HomePage/referral.svg";
import { colors } from "../constants/theme";

const useStyles = makeStyles((_theme) => ({
  bgcolor: {
    background: colors.white,
  },
  textColor: {
    color: colors.primaryDark,
  },
  sectionbg: {
    background: colors.primaryDark,
  },
  sectionBgLight: {
    backgroundColor: colors.referralBg,
  },
  referralBg: {
    backgroundColor: colors.referralBg,
  },
  offenceTabsContainer: {
    display: "flex",
    overflowX: "auto",
    marginBottom: "16px",
    borderBottom: `1px solid ${colors.primary}`,
    paddingBottom: "8px",
    backgroundColor: "transparent",
    "&::-webkit-scrollbar": { height: "8px" },
    "&::-webkit-scrollbar-track": { backgroundColor: colors.referralBg },
    "&::-webkit-scrollbar-thumb": { backgroundColor: colors.primary, borderRadius: "4px" },
    "&::-webkit-scrollbar-thumb:hover": { backgroundColor: colors.primaryDark },
    scrollbarColor: `${colors.primary} ${colors.referralBg}`,
  },
  offenceTab: {
    padding: "8px 16px",
    cursor: "pointer",
    whiteSpace: "nowrap",
    color: "#666",
    borderBottom: "2px solid transparent",
    "&:hover": { color: colors.primaryDark },
  },
  offenceTabSelected: {
    color: colors.primaryDark,
    borderBottom: `2px solid ${colors.primaryDark}`,
    fontWeight: "bold",
  },
  sectionTitle: {
    fontSize: "1.1rem",
    fontWeight: "bold",
    color: colors.primaryDark,
    marginTop: "20px",
    marginBottom: "10px",
  },
  orgCard: {
    backgroundColor: "#fff",
    borderRadius: "10px",
    padding: "15px",
    marginBottom: "10px",
    borderLeft: `4px solid ${colors.primaryDark}`,
    cursor: "pointer",
    "&:hover": { boxShadow: "0 2px 8px rgba(0,0,0,0.1)" },
  },
  orgIcon: {
    fontSize: "1.5rem",
    marginRight: "12px",
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    backgroundColor: colors.primary,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  orgTitle: {
    fontWeight: "bold",
    color: colors.primaryDark,
  },
  orgContact: {
    fontSize: "0.85rem",
    color: "#666",
    marginTop: "2px",
  },
  orgArrow: {
    fontSize: "1.5rem",
    color: "#8E264E",
    fontWeight: "bold",
  },
  noResults: {
    textAlign: "center",
    color: "#666",
    padding: "20px",
  },
  contentContainer: {
    maxWidth: "600px",
    width: "100%",
    padding: "0 20px",
  },
}));

/** Normalize org status to keys matching 3 admin options only: 'government' | 'semi-government' | 'non-government-civil-society'. */
function getOrgStatusKey(org) {
  const key = (org._orgStatusKey || "").toLowerCase().trim();
  if (key) return key;
  const status = org.organizational_status || org.organizationalStatus;
  const text = typeof status === "string" ? status : (Array.isArray(status) && status[0]?.text) ? status[0].text : (status?.text || "");
  const t = (text || "").toLowerCase().trim();
  if (t === "government") return "government";
  if (t === "semi-government") return "semi-government";
  if (t === "non government/ civil society") return "non-government-civil-society";
  return "";
}

function ReferralDirectory() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [openAbout, setOpenAbout] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const { selLanguageId } = useSelector((state) => state.language);
  const { selDistrictId } = useSelector((state) => state.district);
  const { selProvinceId } = useSelector((state) => state.province);
  const { appContent } = useSelector((state) => state.appContent);
  const { offences, organizationsByOffence, selectedOffence } = useSelector((state) => state.organizations);
  const { backToGuide } = useSelector((state) => state.page);
  
  // All text from app translations (display: Government | Non-Government; data has 3 admin options: Government, Semi-government, Non Government/ Civil Society)
  const [referralTitle, setReferralTitle] = useState("");
  const [governmentTitle, setGovernmentTitle] = useState("");
  const [nonGovernmentTitle, setNonGovernmentTitle] = useState("");
  const [noGovtOrgMessage, setNoGovtOrgMessage] = useState("");
  const [noNonGovtOrgMessage, setNoNonGovtOrgMessage] = useState("");

  const handleOpen = () => setOpenAbout(true);
  const handleClose = () => setOpenAbout(false);

  useEffect(() => {
    trackPageView("Referral Directory", { province: selProvinceId, district: selDistrictId, language: selLanguageId });
  }, [selProvinceId, selDistrictId, selLanguageId]);

  // Track if we've already fetched offences to avoid redundant calls
  const hasFetchedOffences = useRef(false);
  // Only the latest fetch should clear loading (avoids stuck loading when region changes)
  const fetchIdRef = useRef(0);

  // Fetch offences on mount - only if not already loaded
  useEffect(() => {
    if (!hasFetchedOffences.current && offences.length === 0) {
      hasFetchedOffences.current = true;
      dispatch(getOffences({ langId: selLanguageId }));
    }
  }, [dispatch, selLanguageId, offences.length]);

  // Auto-select first offence when offences load
  useEffect(() => {
    if (offences?.length > 0 && !selectedOffence) {
      dispatch(setSelectedOffence(offences[0]._id));
    }
  }, [dispatch, offences, selectedOffence]);

  // Fetch organizations when offence is selected
  useEffect(() => {
    if (!selectedOffence) return;
    fetchIdRef.current += 1;
    const thisFetchId = fetchIdRef.current;
    setLoading(true);
    const timeoutMs = 15000;
    const timeoutId = setTimeout(() => {
      if (fetchIdRef.current === thisFetchId) setLoading(false);
    }, timeoutMs);
    dispatch(getOrganizationsByOffence({
      offenceId: selectedOffence,
      provinceId: selProvinceId,
      districtId: selDistrictId,
      langId: selLanguageId,
    })).finally(() => {
      clearTimeout(timeoutId);
      if (fetchIdRef.current === thisFetchId) setLoading(false);
    });
  }, [dispatch, selectedOffence, selProvinceId, selDistrictId, selLanguageId]);

  // Track offence view for admin dashboard
  useEffect(() => {
    if (selectedOffence) {
      trackOffenceView(selectedOffence, {
        province: selProvinceId,
        district: selDistrictId,
        language: selLanguageId,
      });
    }
  }, [selectedOffence, selProvinceId, selDistrictId, selLanguageId]);

  useEffect(() => {
    if (appContent && appContent.length > 0) {
      const title = getContentTitle(appContent, ["Support Providing Organizations", "Referral", "Referral Guide"]);
      const govTitle = getTitleFromAppContent(appContent, "Government");
      const nonGovTitle = getTitleFromAppContent(appContent, "Non-Government");
      const noGovMsg = getTitleFromAppContent(appContent, "No government organizations found");
      const noNonGovMsg = getTitleFromAppContent(appContent, "No non-government organizations found");
      setReferralTitle(title);
      setGovernmentTitle(govTitle);
      setNonGovernmentTitle(nonGovTitle);
      setNoGovtOrgMessage(noGovMsg);
      setNoNonGovtOrgMessage(noNonGovMsg);
    }
  }, [appContent]);

  // Two sections for UI: Government | Non-Government (Semi-government + Non Government/ Civil Society)
  const governmentOrgs = useMemo(() =>
    organizationsByOffence.filter(org => getOrgStatusKey(org) === "government"),
    [organizationsByOffence]
  );

  const nonGovernmentOrgs = useMemo(() =>
    organizationsByOffence.filter(org => {
      const k = getOrgStatusKey(org);
      return k === "semi-government" || k === "non-government-civil-society";
    }),
    [organizationsByOffence]
  );

  const handleOffenceSelect = (offenceId) => {
    dispatch(setSelectedOffence(offenceId));
  };

  const handleOrgClick = (org) => {
    dispatch(setSelectedItem(org));
    dispatch(setSelectedProviderEng("ReferralDirectory")); // Set back navigation target
    dispatch(changeTab("HelplineDetails"));
  };

  const renderOrgCard = (org, isGovernment, uniqueKey) => (
    <div
      key={uniqueKey ?? org._id}
      className={classes.orgCard}
      onClick={() => handleOrgClick(org)}
    >
      <div className="flex items-center">
        <span className={classes.orgIcon}>
          {isGovernment ? "🏛️" : "👤"}
        </span>
        <div className="flex-1">
          <Typography className={classes.orgTitle}>
            {getTranslatedItemTitle(org, selLanguageId) || org.title?.text || (typeof org.title === 'string' ? org.title : '')}
          </Typography>
          {(() => {
            const text = getOrgContactList(org?.contact, selLanguageId)[0];
            return text ? (
              <Typography className={classes.orgContact}>{text}</Typography>
            ) : null;
          })()}
        </div>
        <span className={classes.orgArrow}>›</span>
      </div>
    </div>
  );

  // Get selected offence title for breadcrumb
  const selectedOffenceObj = offences.find(off => off._id === selectedOffence);
  const selectedOffenceTitle = selectedOffenceObj ? getTranslatedItemTitle(selectedOffenceObj, selLanguageId) : '';

  return (
    <div className={`${classes.referralBg} pb-20 flex flex-col`}>
      <div>
        <Header />
        <PageBackButton
          onClick={() => {
            dispatch(setSelectedOffence(null));
            if (backToGuide) {
              dispatch(setBackToGuide(false));
              dispatch(changeTab("GuideStep"));
            } else {
              dispatch(changeTab("ReferralDirectoryEntitiesGrid"));
            }
          }}
        />
        <div className="flex-col md:flex-row flex justify-between">
          <div className=" block md:hidden mb-5">
            <Options handleOpen={handleOpen} />
          </div>
          <div className="flex mx-5 mt-2 content-center items-center">
            <img className="w-6 mr-3 mb-2" src={referralIcon} alt="Support Organizations" />
            <div className="flex flex-col md:flex-row">
              <Typography
                onClick={() => dispatch(changeTab("ReferralDirectoryEntitiesGrid"))}
                className="mr-3 cursor-pointer"
                color={"black"}
                variant="p"
              >
                {referralTitle} {">"}
              </Typography>
              <Typography className="font-bold" color={"black"} variant="p">
                {selectedOffenceTitle}
              </Typography>
            </div>
          </div>
          <div className=" hidden md:block">
            <Options handleOpen={handleOpen} />
          </div>
        </div>
        
        <div className="flex flex-col justify-center content-center items-center my-2 w-full px-3 sm:px-0 sm:w-72 md:w-6/12 mx-auto max-w-full">
          <div className={classes.contentContainer}>
          {/* Horizontal Offence Tabs */}
          <div className={classes.offenceTabsContainer}>
            {offences.map((offence, index) => (
              <div
                key={`${offence._id}-${index}`}
                className={clsx(
                  classes.offenceTab,
                  selectedOffence === offence._id && classes.offenceTabSelected
                )}
                onClick={() => handleOffenceSelect(offence._id)}
              >
                {getTranslatedItemTitle(offence, selLanguageId) || offence.title?.text || (typeof offence.title === 'string' ? offence.title : '')}
              </div>
            ))}
          </div>

          {offences.length === 0 && (
            <div className="flex justify-center py-8">
              <CircularProgress />
            </div>
          )}

          {/* Organizations below - keep content visible during loading with subtle opacity */}
          <div style={{ opacity: loading ? 0.5 : 1, transition: 'opacity 0.2s ease' }}>
            {/* Small loading indicator */}
            {loading && (
              <div className="flex justify-center py-2">
                <CircularProgress size={20} />
              </div>
            )}
            
            {/* Government Organizations */}
            <Typography className={classes.sectionTitle}>
              {governmentTitle}
            </Typography>
            {governmentOrgs.length > 0 ? (
              governmentOrgs.map((org, index) => renderOrgCard(org, true, `gov-${org._id}-${index}`))
            ) : (
              <Typography className={classes.noResults}>
                {noGovtOrgMessage}
              </Typography>
            )}

            {/* Non-Government Organizations (Semi-government + Non Government/ Civil Society) */}
            <Typography className={classes.sectionTitle}>
              {nonGovernmentTitle}
            </Typography>
            {nonGovernmentOrgs.length > 0 ? (
              nonGovernmentOrgs.map((org, index) => renderOrgCard(org, false, `nongov-${org._id}-${index}`))
            ) : (
              <Typography className={classes.noResults}>
                {noNonGovtOrgMessage}
              </Typography>
            )}
          </div>
          </div>
        </div>
      </div>
      <div>
        <ModalAbout open={openAbout} handleClose={handleClose} head="About" />
      </div>
    </div>
  );
}

export default ReferralDirectory;
